package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * inventory 编译期静态展开阶段（RFC §4.5）。
 *
 * 将每个 `kind = "inventory"` 的元素替换为：
 * - 一个 `kind = "box"` 包装容器（layout=absolute、保留原 x/y/positionMode、宽高依据方向计算）
 * - N 个 `kind = "slot"` 子元素（沿 direction 一维排列：
 *   - horizontal：`x = i * cellSize`，`y = 0`
 *   - vertical：`x = 0`，`y = i * cellSize`
 *   slot 命中盒 = cellSize - 2*cellPadding（cellPadding=1，与 slotGrid 对齐）
 * ）
 *
 * 数量来源优先级：
 * 1. count>0 显式指定 → 用 count
 * 2. dynamic=true → 本阶段不展开（留给 B4 运行时）
 * 3. 都不写 → 报错 E321（第一版无法在编译期推断 provider.slotCount）
 */
class InventoryExpanderPass(private val diag: DiagnosticCollector) {

    /** 入口：展开 screen root 中的所有 inventory。 */
    fun expand(screen: ScreenAst): ScreenAst {
        return screen.copy(root = expandElement(screen.root))
    }

    /** 后序递归：先处理 children，再尝试展开当前节点。 */
    private fun expandElement(element: ElementAst): ElementAst {
        val expandedChildren = element.children.map { expandElement(it) }
        val current = element.copy(children = expandedChildren)
        if (current.kind != "inventory") return current
        return expandInventory(current)
    }

    /** 实际展开逻辑：校验 + 计算 + 替换。 */
    private fun expandInventory(inv: ElementAst): ElementAst {
        // dynamic=true 时保留原节点，B4 阶段再处理（不校验 count / direction 等运行时项以外的缺失）
        if (isDynamic(inv)) return inv

        val sourceExpr = findPropValue(inv, "source")
        val directionExpr = findPropValue(inv, "direction")
        val countExpr = findPropValue(inv, "count")

        var hasError = false

        // source 必传（StringLit 非空）
        if (sourceExpr == null || sourceExpr !is ExprAst.Literal.StringLit || sourceExpr.value.isEmpty()) {
            diag.error(DslError.E318_INVENTORY_SOURCE_MISSING, inv.position, inv.id)
            hasError = true
        }

        // direction 必传，且必须是 horizontal / vertical
        val direction = when (directionExpr) {
            null -> {
                diag.error(DslError.E319_INVENTORY_DIRECTION_MISSING, inv.position, inv.id)
                hasError = true
                null
            }
            is ExprAst.Literal.StringLit -> Direction.fromKey(directionExpr.value).also { d ->
                if (d == null) {
                    diag.error(
                        DslError.E320_INVENTORY_DIRECTION_UNKNOWN,
                        directionExpr.position,
                        inv.id,
                        directionExpr.value,
                    )
                    hasError = true
                }
            }
            else -> {
                diag.error(
                    DslError.E320_INVENTORY_DIRECTION_UNKNOWN,
                    directionExpr.position,
                    inv.id,
                    "<non-literal>",
                )
                hasError = true
                null
            }
        }

        // count 可省，但省略时必须 dynamic=true（dynamic=true 已在上面短路）
        val countInt = if (countExpr == null) {
            diag.error(DslError.E321_INVENTORY_COUNT_REQUIRED, inv.position, inv.id)
            hasError = true
            null
        } else {
            asPositiveInt(countExpr).also { v ->
                if (v == null) {
                    // count 存在但非正整数：用独立错误码 E322（与 slotGrid 的 E313/E315 拆分风格一致）
                    diag.error(DslError.E322_INVENTORY_COUNT_INVALID, inv.position, inv.id)
                    hasError = true
                }
            }
        }

        if (hasError || direction == null || countInt == null) return inv

        // 数值类 prop
        val cellSize = (findPropValue(inv, "cellSize") as? ExprAst.Literal.NumberLit)
            ?.value?.toDouble() ?: 18.0
        // cellPadding 固定为 1（RFC §4.5 对齐 slotGrid 默认值）
        val cellPadding = 1.0
        val slotSize = cellSize - 2.0 * cellPadding

        // 生成 N 个 slot
        val slots = buildList {
            for (i in 0 until countInt) {
                add(buildSlot(inv, i, direction, cellSize, slotSize))
            }
        }

        // 包装 box
        val boxProps = buildBoxProps(inv, countInt, direction, cellSize)

        return ElementAst(
            kind = "box",
            id = inv.id,
            props = boxProps,
            children = slots,
            modifiers = inv.modifiers,
            position = inv.position,
        )
    }

    /** 构造单个 slot 子元素。 */
    private fun buildSlot(
        inv: ElementAst,
        index: Int,
        direction: Direction,
        cellSize: Double,
        slotSize: Double,
    ): ElementAst {
        val pos = inv.position
        val x = if (direction == Direction.HORIZONTAL) index * cellSize else 0.0
        val y = if (direction == Direction.VERTICAL) index * cellSize else 0.0

        // 透传共享 prop（仅原 inventory 显式声明的项）
        val shared = SHARED_PROPS.mapNotNull { name ->
            inv.props.firstOrNull { it.name == name }
        }
        val ownProps = mutableListOf<ElementAst.PropAssign>(
            ElementAst.PropAssign("x", ExprAst.Literal.NumberLit(x, pos), pos),
            ElementAst.PropAssign("y", ExprAst.Literal.NumberLit(y, pos), pos),
            ElementAst.PropAssign("index", ExprAst.Literal.NumberLit(index.toLong(), pos), pos),
            ElementAst.PropAssign("slotSize", ExprAst.Literal.NumberLit(slotSize, pos), pos),
        )

        // Stage 14：inventory 上声明 selectedIndex 时，把其表达式与 slot 索引比较，
        // 注入到该 slot 的 selected 属性。slot.selected 是 reactive 的，DSL 中
        // selectedIndex 写 expr (如 local.player.hotbarSlot)，每帧更新自动重渲。
        val selectedIndexExpr = findPropValue(inv, "selectedIndex")
        if (selectedIndexExpr != null) {
            val slotIndexLit = ExprAst.Literal.NumberLit(index.toLong(), pos)
            val cmp = ExprAst.BinaryOp("==", selectedIndexExpr, slotIndexLit, pos)
            ownProps.add(ElementAst.PropAssign("selected", cmp, pos))
        }

        return ElementAst(
            kind = "slot",
            id = "${inv.id}/slot_${index}",
            props = shared + ownProps,
            children = emptyList(),
            modifiers = sharedSlotModifiers(inv),
            position = pos,
        )
    }

    private fun sharedSlotModifiers(inv: ElementAst) =
        inv.modifiers.mapNotNull { modifier ->
            if (modifier.kind != "style") {
                return@mapNotNull null
            }
            val fields = modifier.fields.filter { it.name in SHARED_SLOT_MODIFIER_FIELDS }
            if (fields.isEmpty()) null else modifier.copy(fields = fields)
        }

    /** 构造 box 包装容器的 props 列表。 */
    private fun buildBoxProps(
        inv: ElementAst,
        count: Int,
        direction: Direction,
        cellSize: Double,
    ): List<ElementAst.PropAssign> {
        val pos = inv.position
        val result = mutableListOf<ElementAst.PropAssign>()
        // 保留 inventory 上的几何 / 定位类 prop
        for (pa in inv.props) {
            if (pa.name in BOX_KEEP_PROPS) result.add(pa)
        }
        // layout=absolute
        result.add(ElementAst.PropAssign("layout", ExprAst.Literal.StringLit("absolute", pos), pos))
        val (w, h) = when (direction) {
            Direction.HORIZONTAL -> count * cellSize to cellSize
            Direction.VERTICAL -> cellSize to count * cellSize
        }
        result.add(ElementAst.PropAssign("width", ExprAst.Literal.NumberLit(w, pos), pos))
        result.add(ElementAst.PropAssign("height", ExprAst.Literal.NumberLit(h, pos), pos))
        return result
    }

    // ===== helpers =====

    /** 查 inventory 上某个 prop 的字面量表达式；不存在返回 null。 */
    private fun findPropValue(inv: ElementAst, name: String): ExprAst? =
        inv.props.firstOrNull { it.name == name }?.value

    /** 把表达式当作正整数字面量提取；失败返回 null。 */
    private fun asPositiveInt(expr: ExprAst): Int? {
        if (expr !is ExprAst.Literal.NumberLit) return null
        val d = expr.value.toDouble()
        if (d <= 0.0 || d != d.toInt().toDouble()) return null
        return d.toInt()
    }

    /** dynamic prop 为 BoolLit(true) 即视为动态模式。 */
    private fun isDynamic(inv: ElementAst): Boolean {
        val v = findPropValue(inv, "dynamic") ?: return false
        return v is ExprAst.Literal.BoolLit && v.value
    }

    /** 方向枚举。 */
    private enum class Direction {
        HORIZONTAL, VERTICAL;

        companion object {
            fun fromKey(key: String): Direction? = when (key) {
                "horizontal" -> HORIZONTAL
                "vertical" -> VERTICAL
                else -> null
            }
        }
    }

    companion object {
        /** 透传到每个子 slot 的共享 prop 名单（与 slotGrid 对齐）。 */
        private val SHARED_PROPS = listOf(
            "source",
            "groupId",
            "key",
            "readOnly",
            "allowShiftTransfer",
            "allowDrop",
            "allowPick",
            "slotType",
            "predict",
            "placeholder",
            // Stage 14：让 inventory DSL 一次声明边框 / 选中外框样式，自动透传到每格 slot
            "borderColor",
            "borderWidth",
            "selectedOutlineColor",
            "selectedOutlineWidth",
        )

        /** 以 modifier 形态透传到子 slot 的历史共享视觉字段。 */
        private val SHARED_SLOT_MODIFIER_FIELDS = setOf("borderColor", "borderWidth")

        /** box 包装容器保留的 inventory 原 prop 名单（仅几何/定位类）。 */
        private val BOX_KEEP_PROPS = setOf("x", "y", "positionMode")
    }
}
